import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { MetricsReportWebDocument } from "../../miContenido/metricsReportWeb/MetricsReportWebDocument";
import { ReportWebPreviewViewport } from "../../miContenido/metricsReportWeb/ReportWebPreviewViewport";
import {
  downloadMetricsReportWebPdf,
  METRICS_REPORT_WEB_ROOT_ID,
} from "./metricsReportWebPdfExport";
import {
  DevMetricsPreviewSidebar,
} from "./DevMetricsPreviewSidebar";
import { useDevMetricsPreview } from "./useDevMetricsPreview";

const DevMetricsPdfPreviewWeb: React.FC = () => {
  const preview = useDevMetricsPreview({
    periodMode: "fixed",
    resolveImages: false,
  });
  const [isExportingPdf, setIsExportingPdf] = React.useState(false);

  const previewData = preview.filteredPreviewData;

  const canExportPdf =
    !!previewData &&
    preview.rangeValid &&
    !preview.previewQuery.isFetching &&
    !preview.isResolvingImages &&
    !preview.isInitialLoad;

  const handleExportPdf = React.useCallback(async () => {
    if (!canExportPdf || isExportingPdf) return;
    const root = document.getElementById(METRICS_REPORT_WEB_ROOT_ID);
    if (!root) {
      window.alert("No se encontró el reporte en pantalla.");
      return;
    }

    setIsExportingPdf(true);
    try {
      await downloadMetricsReportWebPdf({
        root,
        label: preview.selectionLabel,
        startDate: preview.startDate,
        endDate: preview.endDate,
      });
    } catch (error) {
      console.error(error);
      window.alert("No se pudo exportar el PDF.");
    } finally {
      setIsExportingPdf(false);
    }
  }, [
    canExportPdf,
    isExportingPdf,
    preview.endDate,
    preview.selectionLabel,
    preview.startDate,
  ]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <DevMetricsPreviewSidebar
        title="Reporte web"
        codeHint="src/miContenido/metricsReportWeb/"
        periodMode="fixed"
        periodGranularity={preview.periodGranularity}
        setPeriodGranularity={preview.setPeriodGranularity}
        periodIds={preview.periodIds}
        setPeriodIds={preview.setPeriodIds}
        fixedPeriods={preview.fixedPeriods}
        rankingPeriodId={preview.rankingPeriodId}
        setRankingPeriodId={preview.setRankingPeriodId}
        rankingWeekPeriods={preview.rankingWeekPeriods}
        channelOptions={preview.channelOptions}
        channelIds={preview.channelIds}
        setChannelIds={preview.setChannelIds}
        startDate={preview.startDate}
        setStartDate={preview.setStartDate}
        endDate={preview.endDate}
        setEndDate={preview.setEndDate}
        programOptions={preview.programOptions}
        selectedProgramIds={preview.selectedProgramIds}
        setSelectedProgramIds={preview.setSelectedProgramIds}
        rangeValid={preview.rangeValid}
        podcastsLoading={preview.podcastsQuery.isLoading}
        podcastsError={preview.podcastsQuery.isError}
        entityOptionsEmpty={preview.channelOptions.length === 0}
        previewQuery={preview.previewQuery}
        handleRefresh={preview.handleRefresh}
        isResolvingImages={preview.isResolvingImages}
        onExportPdf={() => void handleExportPdf()}
        isExportingPdf={isExportingPdf}
        exportPdfDisabled={!canExportPdf}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100vh",
          overflowY: "auto",
        }}
      >
        {preview.isInitialLoad ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1.5,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Cargando datos del informe…
            </Typography>
          </Box>
        ) : previewData ? (
          <Box
            key={`${preview.previewViewerKey}:${preview.selectedProgramIds.join(",")}`}
            sx={{ position: "relative", height: "100%" }}
          >
            {preview.previewQuery.isFetching ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  py: 1,
                  bgcolor: "warning.light",
                  color: "warning.contrastText",
                }}
              >
                <CircularProgress size={16} color="inherit" />
                <Typography variant="caption">Actualizando período…</Typography>
              </Box>
            ) : null}
            <ReportWebPreviewViewport>
              <MetricsReportWebDocument
                data={previewData}
                comparisonGranularity={preview.comparisonGranularity}
                companyId={preview.companyId}
              />
            </ReportWebPreviewViewport>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              Selecciona un canal y un período para ver el reporte.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DevMetricsPdfPreviewWeb;
